/**
 * Shared, presentation-only helpers for the Payroll Components redesign.
 *
 * IMPORTANT: This file contains NO backend changes. It only derives display
 * state (badges, formula status, readiness) from the existing component records
 * returned by /salary/v2/components, and rebuilds the exact same create/update
 * payload the legacy form produced — so every existing API contract is preserved.
 */

/* ── Category presentation ─────────────────────────────────── */
export const CATEGORY_OPTIONS = [
  { value: "earning", label: "Earning", hint: "Adds to gross salary (Basic, HRA, allowances)." },
  { value: "benefit", label: "Benefit (pre-tax)", hint: "Tax-advantaged perks deducted before tax." },
  { value: "deduction", label: "Deduction (post-tax)", hint: "Reduces net pay (loans, advances)." },
  { value: "employer_contribution", label: "Employer contribution", hint: "Employer-side cost (employer PF, gratuity)." },
  { value: "statutory", label: "Statutory", hint: "Mandated by law (PF, ESI, PT, TDS)." },
];

export const CATEGORY_META = {
  earning: { label: "Earnings", tone: "green", badge: "bw-badge-earning" },
  benefit: { label: "Benefits", tone: "purple", badge: "bw-badge-benefit" },
  deduction: { label: "Deductions", tone: "red", badge: "bw-badge-deduction" },
  employer_contribution: { label: "Employer", tone: "blue", badge: "scp-badge-employer" },
  statutory: { label: "Statutory", tone: "orange", badge: "scp-badge-statutory" },
};

export const CATEGORY_TABS = [
  { key: "all", label: "All" },
  { key: "earning", label: "Earnings" },
  { key: "benefit", label: "Benefits" },
  { key: "deduction", label: "Deductions" },
  { key: "employer_contribution", label: "Employer" },
  { key: "statutory", label: "Statutory" },
];

/* The backend only accepts: fixed | formula | system. We keep those values but
   present them with friendly labels. "system" surfaces as Statutory. */
export const CALC_OPTIONS = [
  { value: "fixed", label: "Fixed", hint: "A flat amount or template value. No formula needed." },
  { value: "formula", label: "Formula", hint: "Computed from an expression (e.g. BASIC * 0.4)." },
  { value: "system", label: "Statutory (system engine)", hint: "Driven by the built-in statutory engine via a system code." },
];

export const CALC_META = {
  fixed: { label: "Fixed", badge: "scp-calc-fixed" },
  formula: { label: "Formula", badge: "scp-calc-formula" },
  system: { label: "Statutory", badge: "scp-calc-system" },
  variable: { label: "Variable", badge: "scp-calc-variable" },
};

const KNOWN_EMPLOYEE_DEDUCTION_CODES = new Set([
  "NPS",
  "VPF",
  "INSURANCE",
  "INSURANCE_DED",
  "EMP_INSURANCE",
  "MEDICAL_INSURANCE",
  "PF",
  "PF_EMPLOYEE",
  "EMPLOYEE_PF",
  "ESI",
  "ESI_EMPLOYEE",
  "EMPLOYEE_ESI",
  "PT",
  "PROF_TAX",
  "TDS",
  "LWF",
  "LOSS_OF_PAY",
  "LOP",
  "LOAN_RECOVERY",
  "ADVANCE",
  "ADVANCE_TAKEN",
]);

const CATEGORY_ALIASES = {
  earnings: "earning",
  allowance: "earning",
  allowances: "earning",
  income: "earning",
  deductions: "deduction",
  deduct: "deduction",
  employer: "employer_contribution",
  statutory_deduction: "statutory",
};

export function displayCategory(row) {
  const meta = row?.meta || {};
  const display = String(meta.payroll_display_category || meta.display_category || "")
    .trim()
    .toLowerCase();
  if (display) return CATEGORY_ALIASES[display] || display;

  if (meta.pre_tax_benefit) return "benefit";

  const raw = String(row?.component_category || row?.component_type || "")
    .trim()
    .toLowerCase();
  const code = String(row?.code || row?.system_code || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");

  if (
    KNOWN_EMPLOYEE_DEDUCTION_CODES.has(code) &&
    (!raw || raw === "earning" || raw === "benefit" || raw === "employer_contribution")
  ) {
    return "deduction";
  }

  const normalized = CATEGORY_ALIASES[raw] || raw;
  if (normalized) return normalized;

  return "earning";
}

export function categoryMeta(category) {
  return CATEGORY_META[category] || CATEGORY_META.earning;
}

export function calcMeta(calc, row) {
  if (isVariablePayrollComponent(row)) {
    return CALC_META.variable;
  }
  return CALC_META[calc] || { label: calc || "—", badge: "scp-calc-fixed" };
}

export const VARIABLE_COMPONENT_CODES = {
  OVERTIME: {
    code: "OVERTIME",
    category: "earning",
    badge: "Variable • Overtime Works",
    help: "Overtime is calculated from Overtime Works settings. Calendar overtime hours and extra-time days that pay overtime (Sunday / holiday / week-off) are combined into this one earning — they are not allocated twice.",
    typeLabel: "Variable Payroll Component",
    source: "Overtime Works (organisation payroll settings)",
    calculation: "Hours × Overtime Works OT rate, including extra-time days set to overtime pay",
    payrollBehavior: "Automatically added to employee earnings during payroll processing as a single OVERTIME line",
    settingsPath: "/salary-v2/overtime",
    settingsLabel: "Open Overtime Works",
  },
  OT: {
    code: "OVERTIME",
    category: "earning",
    badge: "Variable • Overtime Works",
    help: "OT is an alias of OVERTIME. Extra time and overtime share this one earning.",
    typeLabel: "Variable Payroll Component",
    source: "Overtime Works (organisation payroll settings)",
    calculation: "Same as OVERTIME — Overtime Works settings",
    payrollBehavior: "Automatically added to employee earnings during payroll processing as a single OVERTIME line",
    settingsPath: "/salary-v2/overtime",
    settingsLabel: "Open Overtime Works",
  },
  ADVANCE_TAKEN: {
    code: "ADVANCE_TAKEN",
    category: "deduction",
    badge: "Variable • Recovery",
    help: "Salary advance recovered through payroll.",
    typeLabel: "Variable Payroll Component",
    source: "Employee Profile → Salary & Packages",
    calculation: "Pending employee advance, recovered once in the applicable payroll",
    payrollBehavior: "Deducted as its own payslip line. After payroll is processed, the same advance is not deducted again.",
  },
};

export function componentCodeOf(row) {
  return String(row?.code || row?.system_code || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
}

export function isOvertimePayrollComponent(row) {
  const code = componentCodeOf(row);
  return code === "OVERTIME" || code === "OT";
}

export function isVariablePayrollComponent(row) {
  const code = componentCodeOf(row);
  if (VARIABLE_COMPONENT_CODES[code]) return true;
  return String(row?.meta?.component_kind || "").toLowerCase() === "variable";
}

export function overlayOvertimeFromExtraWork(row, extraWork) {
  if (!isOvertimePayrollComponent(row) || !extraWork) return row;
  const ot = extraWork.overtime || {};
  let methodLabel = row?.meta?.extra_work_policy?.method_label || "";
  if (ot.method === "percentage_of_component") {
    methodLabel = `${ot.percentage || 0}% of ${ot.salary_component === "OTHER" ? (ot.salary_component_other || "OTHER") : (ot.salary_component || "BASIC")}`;
  } else if (ot.method === "fixed_rate") {
    const unit =
      ot.rate_unit === "per_occurrence"
        ? "per occurrence"
        : ot.rate_unit === "per_event"
          ? "per event"
          : ot.rate_unit === "per_hour"
            ? "per hour"
            : ot.rate_unit || "per hour";
    methodLabel = `₹${ot.fixed_amount || 0} ${unit}`;
  } else if (ot.method === "formula") {
    methodLabel = "Formula based (Extra Work)";
  }
  return {
    ...row,
    description: `Synced from Extra Work & OT: ${methodLabel || "organisation overtime rules"}. Extra-time days that pay overtime are included here — not as a second earning.`,
    meta: {
      ...(row.meta || {}),
      synced_from: "extra_work",
      calculation: methodLabel || row?.meta?.calculation,
      extra_work_policy: {
        ...(row.meta?.extra_work_policy || {}),
        method_label: methodLabel,
        method: ot.method,
      },
    },
  };
}

export function variableComponentMeta(row) {
  return VARIABLE_COMPONENT_CODES[componentCodeOf(row)] || null;
}

const DEDUCTION_TABLE_CATEGORIES = new Set(["deduction", "statutory", "employer_contribution"]);

/** True when a row belongs in the Earnings table (All view). */
export function isEarningSection(row) {
  return !DEDUCTION_TABLE_CATEGORIES.has(displayCategory(row));
}

/** True when a row belongs in the Deductions table (All view). */
export function isDeductionSection(row) {
  return DEDUCTION_TABLE_CATEGORIES.has(displayCategory(row));
}

/* ── Formula / readiness status derivation ─────────────────── */
/**
 * Derives the formula-readiness of a component purely from existing fields:
 *  - formula calc type needs a non-empty formula_expression
 *  - system calc type needs a system_code
 *  - fixed needs nothing
 * Returns: { code, label, tone, requiresFormula, ready }
 *   tone ∈ assigned | missing | none
 */
export function formulaStatus(row) {
  if (isOvertimePayrollComponent(row)) {
    const label = row?.meta?.extra_work_policy?.method_label || row?.meta?.calculation || "Synced from Extra Work";
    return {
      code: "variable",
      label,
      tone: "assigned",
      requiresFormula: false,
      ready: true,
    };
  }
  if (isVariablePayrollComponent(row)) {
    return {
      code: "variable",
      label: "Payroll Input",
      tone: "assigned",
      requiresFormula: false,
      ready: true,
    };
  }
  const calc = row?.calculation_type || "fixed";
  if (calc === "formula") {
    const has = Boolean((row?.formula_expression || "").trim());
    return has
      ? { code: "assigned", label: "Assigned", tone: "assigned", requiresFormula: true, ready: true }
      : { code: "missing", label: "Formula Missing", tone: "missing", requiresFormula: true, ready: false };
  }
  if (calc === "system") {
    const has = Boolean((row?.system_code || "").trim());
    return has
      ? { code: "assigned", label: "Statutory Linked", tone: "assigned", requiresFormula: false, ready: true }
      : { code: "missing", label: "System Code Missing", tone: "missing", requiresFormula: false, ready: false };
  }
  return { code: "none", label: "No Formula Required", tone: "none", requiresFormula: false, ready: true };
}

/* ── Payload building (kept byte-for-byte compatible w/ legacy form) ── */
export function emptyForm(category = "earning") {
  return {
    code: "",
    name: "",
    description: "",
    component_category: category === "all" ? "earning" : category,
    calculation_type: "fixed",
    formula_expression: "",
    system_code: "",
    rounding_rule: { scale: 2 },
    meta: {},
    attendance_proration_mode: "",
    is_active: true,
  };
}

export function rowToForm(row) {
  return {
    code: row.code || "",
    name: row.name || "",
    description: row.description || "",
    component_category: displayCategory(row),
    calculation_type: row.calculation_type || "fixed",
    formula_expression: row.formula_expression || "",
    system_code: row.system_code || "",
    rounding_rule: row.rounding_rule || { scale: 2 },
    meta: row.meta || {},
    attendance_proration_mode: row.meta?.attendance_proration_mode || "",
    is_active: row.is_active !== false,
  };
}

function normalizeCategoryForApi(category) {
  if (category === "benefit") return { component_category: "deduction", metaFlag: true };
  return { component_category: category, metaFlag: false };
}

function buildMeta(form) {
  const { component_category: apiCategory, metaFlag } = normalizeCategoryForApi(form.component_category);
  const m = { ...(form.meta || {}) };
  if (metaFlag) {
    m.payroll_display_category = "benefit";
    m.pre_tax_benefit = true;
  } else if (form.component_category !== "benefit") {
    delete m.payroll_display_category;
    delete m.pre_tax_benefit;
  }
  if (form.component_category === "earning" && form.attendance_proration_mode) {
    m.attendance_proration_mode = form.attendance_proration_mode;
  } else if (form.component_category !== "earning") {
    delete m.attendance_proration_mode;
  }
  return { apiCategory, meta: m };
}

export function buildPayload(form) {
  const showFormula = form.calculation_type === "formula";
  const showSystem = form.calculation_type === "system";
  const { apiCategory, meta } = buildMeta(form);
  return {
    code: form.code.trim().toUpperCase(),
    name: form.name.trim(),
    description: form.description?.trim() || null,
    component_category: apiCategory,
    calculation_type: form.calculation_type,
    formula_expression: showFormula ? form.formula_expression.trim() : null,
    system_code: showSystem ? form.system_code.trim().toUpperCase() : null,
    rounding_rule: form.rounding_rule,
    meta,
    is_active: form.is_active,
  };
}

export function formIsValid(form) {
  const showFormula = form.calculation_type === "formula";
  const showSystem = form.calculation_type === "system";
  return Boolean(
    form.code.trim() &&
      form.name.trim() &&
      (!showFormula || form.formula_expression.trim()) &&
      (!showSystem || form.system_code.trim())
  );
}

export function isDeletable(row) {
  if (isVariablePayrollComponent(row)) return false;
  return row?.meta?.deletable !== false;
}

export function isCodeLocked(row) {
  return isVariablePayrollComponent(row) || row?.meta?.code_locked === true;
}

/* ── Readiness aggregation across the visible component set ──── */
export function buildReadiness(rows) {
  const active = rows.filter((r) => r.is_active !== false);
  const total = active.length;

  const formulaItems = active.filter((r) => r.calculation_type === "formula" || r.calculation_type === "system");
  const formulaReady = formulaItems.filter((r) => formulaStatus(r).ready);
  const formulaPending = formulaItems.length - formulaReady.length;
  const formulaPct = formulaItems.length === 0 ? 100 : Math.round((formulaReady.length / formulaItems.length) * 100);

  const statutoryItems = active.filter(
    (r) => displayCategory(r) === "statutory" || r.calculation_type === "system"
  );
  const statutoryReady = statutoryItems.length > 0 && statutoryItems.every((r) => formulaStatus(r).ready);

  // Pipeline stages. Stages outside this page (templates/structures) are not
  // verifiable here, so they are surfaced as navigational steps in a neutral
  // "review" state rather than fabricated as complete.
  const stages = [
    {
      key: "components",
      label: "Payroll Components",
      done: total > 0,
      detail: total > 0 ? `${total} created` : "None yet",
      verifiable: true,
    },
    {
      key: "formulas",
      label: "Formula Assignment",
      done: formulaPending === 0 && formulaItems.length > 0,
      pct: formulaPct,
      detail:
        formulaItems.length === 0
          ? "No formulas required"
          : formulaPending === 0
          ? "All assigned"
          : `${formulaPending} pending`,
      verifiable: true,
    },
    {
      key: "statutory",
      label: "Statutory",
      done: statutoryReady,
      detail: statutoryItems.length === 0 ? "Not configured" : statutoryReady ? "Linked" : "Needs codes",
      verifiable: true,
    },
    { key: "templates", label: "Salary Templates", done: null, detail: "Review", verifiable: false },
    { key: "structures", label: "Structures", done: null, detail: "Review", verifiable: false },
  ];

  // Readiness = mean of verifiable signals only (never treat missing statutory as 100%).
  const signals = [
    total > 0 ? 100 : 0,
    formulaPct,
    statutoryItems.length === 0 ? 0 : statutoryReady ? 100 : 0,
  ];
  const readiness = Math.round(signals.reduce((a, b) => a + b, 0) / signals.length);

  return {
    total,
    formulaItems: formulaItems.length,
    formulaReady: formulaReady.length,
    formulaPending,
    formulaPct,
    statutoryReady,
    stages,
    readiness,
  };
}

/* Warnings the admin must not miss, derived from real component state. */
export function buildWarnings(rows) {
  const active = rows.filter((r) => r.is_active !== false);
  const warnings = [];

  const missingFormula = active.filter(
    (r) => r.calculation_type === "formula" && !((r.formula_expression || "").trim())
  );
  const missingSystem = active.filter(
    (r) => r.calculation_type === "system" && !((r.system_code || "").trim())
  );

  if (missingFormula.length > 0) {
    warnings.push({
      id: "missing-formula",
      severity: "high",
      title: `${missingFormula.length} component${missingFormula.length > 1 ? "s" : ""} require a calculation formula`,
      body: "Payroll processing cannot run until every formula component has an expression. Assign them below.",
      targets: missingFormula,
    });
  }
  if (missingSystem.length > 0) {
    warnings.push({
      id: "missing-system",
      severity: "high",
      title: `${missingSystem.length} statutory component${missingSystem.length > 1 ? "s" : ""} need a system code`,
      body: "Statutory components must be linked to the engine (e.g. PF, ESI, PT, TDS) before payroll can compute them.",
      targets: missingSystem,
    });
  }
  if (active.length === 0) {
    warnings.push({
      id: "no-components",
      severity: "info",
      title: "No salary components yet",
      body: "Standard earnings (Basic, HRA, SPA) and deductions (PF, PT, TDS) load automatically — refresh if you just opened this page.",
      targets: [],
    });
  }
  return warnings;
}
